import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info, Loader2 } from "lucide-react";

type StatCardSize = "sm" | "lg";
type LoadingVariant = "inline" | "replace";

/** @param tooltip - Optional tooltip text shown via an info icon next to the label */
type StatCardProps = {
  label: string;
  value: string | number;
  loading?: boolean;
  highlight?: boolean;
  size?: StatCardSize;
  loadingVariant?: LoadingVariant;
  loadingLabel?: string;
  tooltip?: string;
};

const sizeStyles: Record<
  StatCardSize,
  { card: string; label: string; value: string }
> = {
  sm: {
    card: "p-3 sm:p-4",
    label: "text-xs text-muted-foreground sm:text-sm",
    value: "text-xl font-semibold sm:text-2xl",
  },
  lg: {
    card: "p-5",
    label:
      "text-xs font-semibold uppercase tracking-wide text-muted-foreground",
    value: "text-3xl font-semibold",
  },
};

/**
 * Displays a labeled metric value inside a card.
 * @param props - StatCard configuration
 * @returns A card element with label, value, optional tooltip, and loading state
 */
export function StatCard({
  label,
  value,
  loading = false,
  highlight = false,
  size = "sm",
  loadingVariant = "replace",
  loadingLabel = "Loading",
  tooltip,
}: StatCardProps) {
  const styles = sizeStyles[size];
  const cardClassName = `${styles.card} ${
    highlight ? "border-blue-200 bg-blue-50" : "border-border"
  }`;

  return (
    <Card className={cardClassName}>
      <div className="flex items-center gap-1">
        <p className={styles.label}>{label}</p>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px]">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {loading && loadingVariant === "replace" ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="hidden sm:inline">{loadingLabel}</span>
        </div>
      ) : (
        <div className={`flex items-center gap-2 ${styles.value}`}>
          <span>{value}</span>
          {loading && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
        </div>
      )}
    </Card>
  );
}
